import { Typography } from '@/components/ui/typography';
import { formatPriceDisplay } from '@/util/formatting';
import { cn } from '@/lib/utils';
import { CardTypeIcon } from '@/components/orders/payment/utils/cardHelpers';
import PromoCodeInput from '@/components/orders/payment/components/PromoCodeInput';
import type { ServiceType } from '@/components/ui/carousel';
import type { useAssetServiceOrder } from './useAssetServiceOrder';
import type { useAssetOrderPayment } from './useAssetOrderPayment';
import type { useAssetOrderPromo } from './useAssetOrderPromo';

export const priceText = (n: number) =>
  n === 0 ? 'Free' : `$${formatPriceDisplay(n)}`;
const last4 = (n: string) => n.replace(/\D/g, '').slice(-4);

export type OrderStep = 'main' | 'paying' | 'placing' | 'done';

/**
 * One prop bag, two presentations: OrderServiceSummary renders the read-only
 * summary content and OrderServiceActions the step-driven CTA stack. They are
 * separate components because the sub-lg sheet scrolls the summary inside its
 * body while PINNING the actions to a footer (2026-08-21 on-device finding),
 * whereas the desktop dialog renders both in its right column, in this order,
 * exactly as before. Keeping one shared type stops the two halves' inputs from
 * drifting apart.
 */
export type OrderServicePaneProps = {
  service: ServiceType;
  order: ReturnType<typeof useAssetServiceOrder>;
  pay: ReturnType<typeof useAssetOrderPayment>;
  promo: ReturnType<typeof useAssetOrderPromo>;
  step: OrderStep;
  setStep: (step: OrderStep) => void;
  payView: 'current' | 'list';
  setPayView: (view: 'current' | 'list') => void;
  effectiveTotal: number;
  effectiveFree: boolean;
  showCardUi: boolean;
  submitButtonLabel: string;
  onSubmit: () => void;
  onConfirmPay: () => void;
  onAddCard: () => void;
  onClose: () => void;
};

/**
 * The Order Summary content — totals, promo, and the saved-card picker. The
 * CTA stack that used to live here is OrderServiceActions (2026-08-21): the
 * sheet pins it while this scrolls. Both presentations share this component,
 * so step logic and gating still live once.
 */
export default function OrderServiceSummary({
  service,
  order,
  pay,
  promo,
  step,
  payView,
  setPayView,
  effectiveTotal,
  effectiveFree,
  showCardUi,
  onAddCard
}: OrderServicePaneProps) {
  return (
    <>
      {/* as="h2" matches the left service-name heading (also h2) so the
          two headings share identical box metrics and sit on the same
          baseline; a default inline <span> would offset slightly. */}
      <Typography variant="heading-md" as="h2">
        Order Summary
      </Typography>
      <div className="mt-3 flex flex-col gap-2 text-sm">
        <div className="flex justify-between">
          <span>{service.name}</span>
          <span>
            {order.count} × {priceText(order.unitPrice)}
          </span>
        </div>
        <div className="flex justify-between">
          <span>Subtotal</span>
          <span>{priceText(order.subtotal)}</span>
        </div>

        {(step === 'paying' || step === 'placing') && !order.isFree && (
          <PromoCodeInput
            promoCode={promo.promoCode}
            onPromoCodeChange={promo.setPromoCode}
            onApply={promo.apply}
            onRemove={promo.remove}
            isPromoApplied={promo.isApplied}
            isPromocodeInvalid={promo.isInvalid}
            promocodeMessage={promo.message}
            isLoading={promo.loading}
            inputResetKey={promo.inputResetKey}
          />
        )}
        {(step === 'paying' || step === 'placing') && promo.isApplied && (
          <div className="flex justify-between text-success">
            <span>Discount</span>
            <span>-${formatPriceDisplay(promo.savings)}</span>
          </div>
        )}

        <div className="mt-2 flex justify-between border-t border-border pt-2 font-semibold">
          <span>Total</span>
          <span>
            {priceText(step === 'main' ? order.subtotal : effectiveTotal)}
          </span>
        </div>
      </div>

      {(step === 'paying' || step === 'placing') && (
        <div className="mt-4">
          {showCardUi && payView === 'current' && pay.selectedCard && (
            <div className="flex items-center justify-between rounded-md border border-border p-3 text-sm">
              <span className="flex items-center gap-2">
                <CardTypeIcon type={pay.selectedCard.cardType} />
                •••• {last4(pay.selectedCard.cardNumber)}
              </span>
              <button
                type="button"
                className="text-primary"
                disabled={step === 'placing'}
                onClick={() => setPayView('list')}
              >
                Change
              </button>
            </div>
          )}
          {showCardUi && payView === 'list' && (
            <div
              role="radiogroup"
              aria-label="Saved cards"
              className="flex flex-col gap-2"
            >
              {pay.cards.map((card) => {
                const selected =
                  card.customerPaymentProfileId ===
                  pay.selectedCard?.customerPaymentProfileId;
                return (
                  <button
                    key={card.customerPaymentProfileId}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    aria-label={`${card.cardType} •••• ${last4(
                      card.cardNumber
                    )}`}
                    onClick={() => {
                      pay.setSelectedCard(card);
                      setPayView('current');
                    }}
                    className={cn(
                      'flex items-center justify-between rounded-md border p-3 text-sm',
                      selected ? 'border-primary' : 'border-border'
                    )}
                  >
                    <span className="flex items-center gap-2">
                      <CardTypeIcon type={card.cardType} />
                      •••• {last4(card.cardNumber)}
                    </span>
                    {selected && <span className="text-primary">✓</span>}
                  </button>
                );
              })}
              <button
                type="button"
                className="text-left text-sm text-primary"
                onClick={onAddCard}
              >
                + Add a Card
              </button>
              <button
                type="button"
                className="mt-1 text-sm text-muted-foreground"
                onClick={() => setPayView('current')}
              >
                Cancel
              </button>
            </div>
          )}
          {effectiveFree && (
            <Typography variant="body" className="text-center">
              No charge for this order
            </Typography>
          )}
          {!effectiveFree && pay.isSkipCheckout && (
            <Typography variant="body" className="text-center">
              Billed to your monthly invoice
            </Typography>
          )}
        </div>
      )}
    </>
  );
}
